import React, { Component } from "react";
import { EjariContractContext, updateContractContext } from "@eres/ejari-core/contexts/EjariContractContext";
import { WebFormFieldProps } from "@eres/forms-web";
import StateHolder from "@eres/forms/StateHolder";
import { defaultErrorManager } from "@eres/forms/errormanager";
import Strings, { LVStrings } from "@my-dld/assets/strings";
import FormRow from "@eres/forms-web/FormRow";
import FormSection from "@my-dld/components/Form/FormSection";
import { LoadableDataState } from "@my-dld/utils/UIUtils";
import CurrencyInputField from "@eres/forms-web/CurrencyInputField";
import { valueRenderer } from "./ContractCalculatedValue";
import CountInputField from "@eres/forms-web/CountInputField";
import FormAction from "@my-dld/components/Form/FormAction";
import { RoutedViewProps } from "@my-dld/utils/UrlUtils";
import { ContractPayment, PaymentType } from "@eres/ejari-api/Entity/contract";
import FormSubSection, { SectionAction } from "../../../../eres/forms-web/FormSubSection";
import Popup from "../../../../components/Popup/Popup";
import RadioInputField from "@eres/forms-web/RadioInputField";
import {
	IdExtractor,
	IdExtractorFallback,
	lookupConverter,
	lookupDisplayConverter,
	serverDateConverter,
	serverDisplayDateConverter
} from "@my-dld/utils/LookupUtil";
import TextInputField from "@eres/forms-web/TextInputField";
import { Lookup } from "@eres/ejari-api/Entity";
import PickerInput from "@eres/forms-web/PickerInput";
import { SpinnerLoader } from "@my-dld/components/SpinnerLoader";
import { EjariLookUp } from "@eres/ejari-api/constants";
import { AlphaSortProcessor, populateLookupOnTo } from "@eres/ejari-api/Utils";
import DateInputField from "@eres/forms-web/DateInputField";
import { NonZeroValidator } from "@eres/forms/validator/NonZeroValidator";
import _ from "lodash";
import { ErrorContainer } from "@eres/forms";
import { checkNonEmpty } from "../../utils";
import BigNumber from "bignumber.js";
import { CurrencyUtil, LocaleUtil, ServerDate } from "@eres/core";
import { navigate } from "@my-dld/utils/StoreUtils";
import ContractItems, { EditAction, ItemViewProps, NewItemProps, NewItemState, RemoveAction } from "./ContractItems";

interface Props extends RoutedViewProps {
}


interface FormState {
	ContractValue?: number;
	SecurityDeposit?: number;
	NoOfPayments: number;
	PaymentPlan?: ContractPayment[];
}

interface State extends LoadableDataState, FormState {
	canAddMorePayments: boolean;
	isValid: boolean;
}

class PaymentInfo extends Component<Props, State> {
	static contextType = EjariContractContext;
	context!: React.ContextType<typeof EjariContractContext>;

	itemsView = React.createRef<ContractItems<ContractPayment>>();

	constructor( props: Props ) {
		super( props );
		this.state = {
			loadState: "loading",
			errorContainer: {},
			canAddMorePayments: true,
			NoOfPayments: 1,
			isValid: false,
		};
	}

	componentDidMount() {
		let ctx = this.context;
		const f = ctx.finance;
		const update: Partial<State> = {};
		if ( f ) {
			update.ContractValue = f.ContractActualValue;
		}
		update.NoOfPayments = ctx.NoOfPayments;
		update.SecurityDeposit = ctx.SecurityDeposit as any;
		update.PaymentPlan = ctx.Payments || [];
		this.setState( update as State );
		if ( LookupCache.Banks == null || LookupCache.Banks.length == 0 ) {
			populateLookupOnTo( EjariLookUp.BANKS, {
				onLookup:
					( lookup: EjariLookUp | string, values: Lookup[] ) => {
						LookupCache.Banks = values;
						this.setState( {
							loadState: "done"
						} );
					}
			}, undefined, AlphaSortProcessor );
		} else {
			this.setState( {
				loadState: "done"
			} );
		}
	}

	buildItemForAddNew = () => {
		return this.getNextPayment();
	};
	onAddNew = () => {
		this.itemsView.current?.onAddNewItem();
	};
	onItemsChanged = ( items: ContractPayment[] ) => {
		this.setState( {
			PaymentPlan: items
		} );
	};
	renderItem = ( props: ItemViewProps<ContractPayment>, state: ContractPayment, fieldProps: Partial<WebFormFieldProps<any, any>> ) => {
		const { position } = props;

		const isCheque = state.PaymentTypeId === PaymentType.CHEQUE;
		const title = position + ". " + ( isCheque ? Strings.cheque_payment : Strings.cash_payment );
		const onAction = ( actionId: string, action: SectionAction ) => {
			this.onItemAction( actionId, { ...action, data: props.data } );
		};
		const actions: SectionAction[] = [ EditAction ];
		const canRemove = position === this.state.PaymentPlan?.length;
		if ( canRemove ) actions.push( RemoveAction );
		return <FormSubSection
			title={ title }
			onAction={ onAction }
			actions={ actions }>
			{ isCheque && <FormRow { ...fieldProps }>
				<TextInputField
					mode={ "text" }
					id={ "ChequeNumber" }
					textRenderer={ valueRenderer as any }
					label={ Strings.chq_nb }
				/>
				<PickerInput
					mode={ "text" }
					textRenderer={ valueRenderer as any }
					isLoading={ LookupCache.Banks === undefined }
					id={ "BankId" }
					toIdentifier={ IdExtractorFallback }
					valueConverter={ lookupDisplayConverter }
					label={ Strings.chq_bank_name }
					items={ LookupCache.Banks }
				/>
			</FormRow>
			}
			<FormRow { ...fieldProps }>
				<TextInputField
					mode={ "text" }
					textRenderer={ valueRenderer as any }
					label={ Strings.due_date }
					id={ "InstallmentDueDate" }
					valueConverter={ serverDisplayDateConverter }
				/>
				<CurrencyInputField
					mode={ "text" }
					textRenderer={ valueRenderer }
					id={ "Amount" }
					label={ Strings.amount }/>
			</FormRow>
		</FormSubSection>;
	};
	onItemAction = ( actionId: string, action: SectionAction ) => {
		const data = action.data as ContractPayment;
		switch ( actionId ) {
			case "edit":
				this.itemsView.current?.onEditItem( data );
				break;
			case "delete":
				const np = this.state.PaymentPlan?.filter( x => x != data );
				this.setState( { PaymentPlan: np } );
				break;
		}
	};
	validateItem = ( isValid: boolean, data: ContractPayment, errorContainer: ErrorContainer, props: NewItemProps<ContractPayment>, state: NewItemState<ContractPayment> ) => {
		isValid = isValid && checkNonEmpty( data, "Amount", errorContainer );
		isValid = isValid && checkNonEmpty( data, "InstallmentDueDate", errorContainer );
		isValid = isValid && checkNonEmpty( data, "PaymentTypeId", errorContainer );
		if ( data.PaymentTypeId === PaymentType.CHEQUE ) {
			isValid = isValid && checkNonEmpty( data, "BankId", errorContainer );
			isValid = isValid && checkNonEmpty( data, "ChequeNumber", errorContainer );
		}
		return isValid;
	};
	extractUpdatedData = ( props: NewItemProps<ContractPayment>, state: NewItemState<ContractPayment> ) => {
		const data: ContractPayment = _.omit( state, "isValid", "errorContainer", );
		if ( data.PaymentTypeId === PaymentType.CHEQUE && LookupCache.Banks ) {
			data.BankName = LocaleUtil.getLocalValue( LookupCache.Banks.find( x => x.Id === data.BankId ) );
		}
		return data;
	};
	renderForm = ( props: NewItemProps<ContractPayment>, state: NewItemState<ContractPayment>, fieldProps: Partial<WebFormFieldProps<any, any>>, onDone: () => void ) => {
		const { isEdit, } = props;
		const { isValid } = state;
		const isCheque = state.PaymentTypeId === PaymentType.CHEQUE;
		const showCash = this.state.PaymentPlan == null || this.state.PaymentPlan.length === 0;
		fieldProps = {
			...fieldProps,
			containerClassName: "col-md-12 form-group",
		};
		return <Popup
			title={ Strings.add_payment }
			showIcon={ false }
			clickOutsideToDismiss={ false }
			id={ props.dialogId } modalBodyContainer="" modalSubContainer="modal-dialog modal-dialog-centered modal-ejari-contract-payment">
			<div className={ "modal-body" }>

				{ showCash && <>
					<FormRow { ...fieldProps }>
						<RadioInputField
							required={ true }
							isLoading={ PaymentModes == null }
							id={ "PaymentTypeId" }
							toIdentifier={ IdExtractor }
							valueConverter={ lookupConverter }
							hideLabel={ true } items={ PaymentModes }
						/>
					</FormRow>
				</> }
				{ isCheque && <>
					<FormRow { ...fieldProps }>
						<TextInputField
							required={ true }
							id={ "ChequeNumber" }
							containerClassName="col-md-12 form-group"
							label={ Strings.chq_nb }
						/>
						<PickerInput
							required={ true }
							isLoading={ LookupCache.Banks === undefined }
							id={ "BankId" }
							toIdentifier={ IdExtractorFallback }
							valueConverter={ lookupConverter }
							label={ Strings.chq_bank_name }
							items={ LookupCache.Banks }
						/>
					</FormRow>
				</> }
				<FormRow { ...fieldProps }>
					<DateInputField
						required={ true }
						label={ Strings.due_date }
						id={ "InstallmentDueDate" }
						valueConverter={ serverDateConverter }
					/>
					<CurrencyInputField
						required={ true }
						id={ "Amount" }
						valueValidators={ NonZeroValidator }
						label={ Strings.amount }/>


				</FormRow>
			</div>
			<FormAction
				groupClassName=""
				disablePrimaryAction={ !isValid }
				primaryActionLabel={ isEdit ? Strings.update : Strings.add }
				onPrimaryAction={ onDone }
			/>
		</Popup>;
	};

	render() {

		const {
			canAddMorePayments,
			PaymentPlan,
			loadState,
			isValid,
			errorContainer
		} = this.state;
		if ( loadState === "loading" ) return <SpinnerLoader/>;
		const filedProps: Partial<WebFormFieldProps<any, any>> = {
			holder: StateHolder.buildStateHolder( this ),
			containerClassName: "col-md-4 form-group",
			errorManager: defaultErrorManager,
		};
		return (
			<>
				<FormSection title={ Strings.payment_information }>
					<FormRow { ...filedProps }>
						<CurrencyInputField
							textRenderer={ valueRenderer }
							mode={ "text" }
							id={ "ContractValue" }
							label={ Strings.contract_amount }/>
						<CurrencyInputField
							id={ "SecurityDeposit" }
							label={ Strings.security_deposit }/>
						<CountInputField
							minValue={ 1 }
							maxValue={ 100 }
							containerClassName="number-of-pay"
							id={ "NoOfPayments" }
							label={ Strings.nb_payments }>

							<>
								{ canAddMorePayments &&
									<FormAction
										actionContainerClassName={ "add-payment-link" }
										primaryActionClassName="btn btn-link ps-0 pe-0"
										primaryActionLabel={ Strings.add_payment }
										onPrimaryAction={ this.onAddNew }
									/>
								}
							</>
						</CountInputField>
					</FormRow>

					<ContractItems
						dialogId={ "NewTerms" }
						ref={ this.itemsView }
						items={ PaymentPlan }
						onItemsChanged={ this.onItemsChanged }
						renderItem={ this.renderItem }
						buildItemForAddNew={ this.buildItemForAddNew }
						validateItem={ this.validateItem }
						extractUpdatedData={ this.extractUpdatedData }
						renderForm={ this.renderForm }
					/>
					{ errorContainer[ "Payments" ] && <div className={ "invalid-feedback invalid-feedback-x" }>
						{ errorContainer[ "Payments" ] }
					</div> }
				</FormSection>
				<FormRow>
					<FormAction
						disablePrimaryAction={ !isValid }
						groupClassName={ "text-center" }
						primaryActionLabel={ Strings.continue }
						onPrimaryAction={ this.onContinue }
						secondaryActionLabel={ Strings.back }
						onSecondaryAction={ this.onBack }
					/>
				</FormRow>
			</>
		);
	}

	onContinue = () => {
		const d = this.getUpdatedData();
		updateContractContext( {
			SecurityDeposit: d.SecurityDeposit + "",
			NoOfPayments: d.NoOfPayments,
			Payments: d.PaymentPlan,
		} );
		navigate( "terms" );
	};

	getNextPayment = () => {
		let pc = this.state.NoOfPayments!;
		let payments = this.state.PaymentPlan!;
		let ec = payments != undefined ? payments.length : 0;
		let remaining = pc - ec;
		let amountX: BigNumber | undefined = undefined;
		let dateX: ServerDate | undefined = undefined;
		if ( remaining > 0 ) {
			let ta = new BigNumber( this.state.ContractValue! );
			let ca = new BigNumber( 0 );
			amountX = new BigNumber( ta );
			if ( payments != undefined )
				for ( const payment of payments ) {
					ca = ca.plus( new BigNumber( payment.Amount! ) );
				}
			if ( ta.isGreaterThan( ca ) ) {
				amountX = ta.minus( ca );
				amountX = amountX.dividedBy( remaining );
				amountX = new BigNumber( CurrencyUtil.convertCurrencyToValue( amountX.toString() )! );
			} else {
				amountX = undefined;
			}
			let start = this.context.finance!.StartDate;
			let end = this.context.finance!.EndDate;
			if ( payments != undefined && payments.length !== 0 ) {
				start = payments[ payments.length - 1 ].InstallmentDueDate;
				let timeMills = end!.asDate().getTime() - start!.asDate().getTime();
				let interVal = timeMills / ( remaining + 1 );
				dateX = new Date( start!.asDate().getTime() + interVal ).asServerString();
			} else {
				dateX = start;
			}
		}
		return {
			Amount: amountX ? amountX.toString() : undefined,
			InstallmentDueDate: dateX,
			PaymentTypeId: ec > 0 ? PaymentType.CHEQUE : PaymentType.CASH
		};
	};

	onBack = () => {
		this.props.history.goBack();
	};
	isValid = () => {
		let isValid = true;
		const errorContainer: ErrorContainer = {};
		const nd = this.getUpdatedData();
		isValid = isValid && checkNonEmpty( nd, "SecurityDeposit", errorContainer );
		isValid = isValid && checkNonEmpty( nd, "NoOfPayments", errorContainer );

		if ( isValid && nd.PaymentPlan && nd.PaymentPlan.length > 0 ) {
			const l = nd.PaymentPlan.length;
			if ( l != nd.NoOfPayments ) {
				isValid = false;
				errorContainer[ "Payments" ] = Strings.payment_nb_not_valid;
			}
			if ( isValid ) {
				const rv = new BigNumber( nd.ContractValue! );
				const v = nd.PaymentPlan.reduce( ( previousValue, currentValue ) => {
					return previousValue.plus( new BigNumber( currentValue.Amount! ) );
				}, new BigNumber( 0 ) );
				if ( !rv.isEqualTo( v ) ) {
					isValid = false;
					errorContainer[ "Payments" ] = Strings.payment_value_not_valid;
				}
			}
		}
		requestAnimationFrame( () => {
			this.setState( {
				errorContainer: errorContainer,
			} );
		} );

		return isValid;
	};

	getUpdatedData = ( state: State = this.state ) => {
		const data: FormState = _.pick( state, "ContractValue", "SecurityDeposit", "NoOfPayments", "PaymentPlan" );
		return data;
	};

	componentDidUpdate( prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any ) {
		const od = this.getUpdatedData( prevState );
		const nd = this.getUpdatedData( this.state );
		if ( !_.isEqual( od, nd ) ) {
			this.updateValidity();
		}
	}

	updateValidity = () => {
		this.setState( s => {
			let count = s.NoOfPayments!;
			let itemsCount = s.PaymentPlan ? s.PaymentPlan.length : 0;
			let pp = s.PaymentPlan;
			if ( pp && itemsCount > count ) {
				pp = pp.slice( 0, count );
			}
			return {
				isValid: this.isValid(),
				canAddMorePayments: count > itemsCount,
				PaymentPlan: pp,
			};
		} );
	};
}

export default PaymentInfo;


const CashPayment: Lookup = {
	Id: PaymentType.CASH,
	...LVStrings.cash,
};
const ChequePayment: Lookup = {
	Id: PaymentType.CHEQUE,
	...LVStrings.cheque,
};

const PaymentModes = [ CashPayment, ChequePayment ];

const LookupCache: {
	Banks?: Lookup[]
} = {};
